/**
 * Pharmaceutical Compliance Checker
 * Validates assets against FDA and BRD requirements
 */

interface BRDRule {
  id: number
  rule: string
  severity: 'critical' | 'warning' | 'minor'
  field: string
  section: string
  description: string
}

interface Asset {
  id: string
  name: string
  type: string
  size: number
}

interface ComplianceIssue {
  id?: number
  rule: string
  severity: 'critical' | 'warning' | 'minor' | 'info'
  field: string
  section: string
  channel: string
  status: 'pass' | 'fail' | 'warning' | 'not_checked'
  message: string
  detectedValue?: string
  expectedValue?: string
  source?: 'Built-in' | 'Custom BRD' | 'AI Analysis'
  reasoning?: string
  // Video timestamps (from Gemini)
  timestamp?: string
  timestampSeconds?: number
  endTimestamp?: string
  endTimestampSeconds?: number
  duration?: number
  videoFrame?: string
  audioContent?: string
}

interface ComplianceResult {
  campaignName: string
  fdaFilingNumber: string
  customerType: string
  complianceScore: number
  issues: ComplianceIssue[]
  recommendations: string[]
  summary: {
    totalChecks: number
    passed: number
    failed: number
    warnings: number
  }
}

export function processBRDCompliance(
  aiResult: any,
  asset: Asset,
  customBRDRules: BRDRule[] = []
): ComplianceResult {
  const issues: ComplianceIssue[] = []

  // Check if AI analysis is available
  const aiAvailable = aiResult && Object.keys(aiResult).length > 0 && (aiResult.detectedContent || aiResult.complianceIssues)

  console.log('[Compliance] Starting pharmaceutical compliance checks for', asset.name, {
    aiAvailable,
    aiResultKeys: aiResult ? Object.keys(aiResult) : [],
    customRulesCount: customBRDRules.length,
    assetType: asset.type,
    hasGeminiData: !!aiResult?.complianceIssues,
    hasDetectedContent: !!aiResult?.detectedContent
  })

  // If no AI available, provide helpful message
  if (!aiAvailable) {
    console.warn('⚠️ AI analysis not available - compliance checks limited to manual review')
    console.warn('💡 Configure OPENAI_API_KEY or GEMINI_API_KEY for automated analysis')
  }

  // ==========================================
  // STANDARD PHARMACEUTICAL COMPLIANCE RULES
  // ==========================================

  // Rule 1: Fair Balance (Critical)
  if (aiAvailable) {
    const hasFairBalance = aiResult.detectedContent?.complianceElements?.some((el: string) =>
      el.toLowerCase().includes('risk') || el.toLowerCase().includes('side effect')
    ) || aiResult.complianceIssues?.some((issue: any) => 
      issue.rule?.toLowerCase().includes('fair balance') && issue.status === 'pass'
    )
    
    issues.push({
      rule: 'Fair Balance',
      severity: 'critical',
      field: 'fairBalance',
      section: 'FDA Compliance',
      channel: 'Social',
      status: hasFairBalance ? 'pass' : 'warning',
      message: hasFairBalance 
        ? 'Fair balance between benefits and risks detected'
        : 'Limited risk information detected - review fair balance',
      detectedValue: hasFairBalance ? 'Benefits and risks presented' : 'Primarily benefits shown',
      expectedValue: 'Balanced presentation of benefits and risks',
      source: 'AI Analysis',
      reasoning: 'AI analysis checked for presence of risk/safety information alongside benefits'
    })
  } else {
    issues.push({
      rule: 'Fair Balance',
      severity: 'critical',
      field: 'fairBalance',
      section: 'FDA Compliance',
      channel: 'Social',
      status: 'warning',
      message: '⚠️ Manual review required - AI analysis not configured',
      detectedValue: 'AI not available',
      expectedValue: 'Configure OPENAI_API_KEY or GEMINI_API_KEY',
      reasoning: 'This rule needs AI to analyze benefit/risk balance in content',
      source: 'Built-in'
    })
  }

  // Rule 2: ISI Present (Critical)
  if (aiAvailable) {
    const hasISI = aiResult.detectedContent?.complianceElements?.some((el: string) =>
      el.toLowerCase().includes('isi') || el.toLowerCase().includes('important safety') || 
      el.toLowerCase().includes('side effects')
    ) || aiResult.complianceIssues?.some((issue: any) => 
      issue.rule?.toLowerCase().includes('isi') && issue.status === 'pass'
    )
    
    issues.push({
      rule: 'ISI Present',
      severity: 'critical',
      field: 'isi',
      section: 'FDA Compliance',
      channel: 'Social',
      status: hasISI ? 'pass' : 'warning',
      message: hasISI 
        ? 'Important Safety Information (ISI) detected'
        : 'ISI should be reviewed - ensure adequate safety information',
      detectedValue: hasISI ? 'ISI Present' : 'ISI May Need Enhancement',
      expectedValue: 'Comprehensive ISI Required',
      source: 'AI Analysis',
      reasoning: 'AI analysis searched for Important Safety Information elements'
    })
  } else {
    issues.push({
      rule: 'ISI Present',
      severity: 'critical',
      field: 'isi',
      section: 'FDA Compliance',
      channel: 'Social',
      status: 'warning',
      message: '⚠️ Manual review required - AI analysis not configured',
      detectedValue: 'AI not available',
      expectedValue: 'Configure OPENAI_API_KEY or GEMINI_API_KEY',
      reasoning: 'Requires AI analysis to verify ISI presence',
      reasoning: 'This rule needs AI to detect Important Safety Information',
      source: 'Built-in'
    })
  }

  // Rule 3: Indication (Critical)
  if (aiAvailable) {
    const hasIndication = aiResult.detectedContent?.therapeuticArea || 
      aiResult.detectedContent?.complianceElements?.some((el: string) =>
        el.toLowerCase().includes('indicated') || el.toLowerCase().includes('treatment')
      )
    
    issues.push({
      rule: 'Indication',
      severity: 'critical',
      field: 'indication',
      section: 'FDA Compliance',
      channel: 'Social',
      status: hasIndication ? 'pass' : 'warning',
      message: hasIndication 
        ? 'Product indication clearly stated'
        : 'Indication should be verified for clarity',
      detectedValue: hasIndication ? 'Indication Present' : 'Needs Review',
      expectedValue: 'Clear indication statement',
      source: 'AI Analysis',
      reasoning: 'AI analysis checked for indication/treatment statements'
    })
  } else {
    issues.push({
      rule: 'Indication',
      severity: 'critical',
      field: 'indication',
      section: 'FDA Compliance',
      channel: 'Social',
      status: 'warning',
      message: '⚠️ Manual review required - AI analysis not configured',
      detectedValue: 'AI not available',
      expectedValue: 'Configure OPENAI_API_KEY or GEMINI_API_KEY',
      reasoning: 'Requires AI analysis to verify indication',
      reasoning: 'This rule needs AI to detect product indication',
      source: 'Built-in'
    })
  }

  // Rule 4: Brand Usage (Critical)
  if (aiAvailable) {
    const hasBrand = aiResult.detectedContent?.brandName || aiResult.campaignSuggestions?.campaignName
    issues.push({
      rule: 'Brand Usage',
      severity: 'critical',
      field: 'brand',
      section: 'Creative Requirements',
      channel: 'Social',
      status: hasBrand ? 'pass' : 'warning',
      message: hasBrand 
        ? `Brand identified: ${hasBrand}`
        : 'Brand name should be verified for visibility',
      detectedValue: hasBrand || 'Needs Review',
      expectedValue: 'Brand name prominently displayed',
      source: 'AI Analysis',
      reasoning: 'AI analysis detected brand name in visual or text content'
    })
  } else {
    issues.push({
      rule: 'Brand Usage',
      severity: 'critical',
      field: 'brand',
      section: 'Creative Requirements',
      channel: 'Social',
      status: 'warning',
      message: '⚠️ Manual review required - AI analysis not configured',
      detectedValue: 'AI not available',
      expectedValue: 'Configure OPENAI_API_KEY or GEMINI_API_KEY',
      reasoning: 'Requires AI analysis to verify brand usage',
      reasoning: 'This rule needs AI to detect brand elements',
      source: 'Built-in'
    })
  }

  // Rule 5: Contraindications (Critical)
  if (aiAvailable) {
    const hasContraindications = aiResult.detectedContent?.complianceElements?.some((el: string) =>
      el.toLowerCase().includes('contraindication') || el.toLowerCase().includes('do not use')
    )
    
    issues.push({
      rule: 'Contraindications',
      severity: 'critical',
      field: 'contraindications',
      section: 'FDA Compliance',
      channel: 'Social',
      status: hasContraindications ? 'pass' : 'warning',
      message: hasContraindications 
        ? 'Contraindications mentioned'
        : 'Verify contraindications are addressed if applicable',
      detectedValue: hasContraindications ? 'Present' : 'Not detected',
      expectedValue: 'Contraindications disclosed if applicable',
      source: 'AI Analysis',
      reasoning: 'AI analysis checked for contraindication statements'
    })
  } else {
    issues.push({
      rule: 'Contraindications',
      severity: 'critical',
      field: 'contraindications',
      section: 'FDA Compliance',
      channel: 'Social',
      status: 'warning',
      message: '⚠️ Manual review required - AI analysis not configured',
      detectedValue: 'AI not available',
      expectedValue: 'Configure OPENAI_API_KEY or GEMINI_API_KEY',
      reasoning: 'Requires AI analysis to verify contraindications',
      reasoning: 'This rule needs AI to detect contraindication info',
      source: 'Built-in'
    })
  }

  // Rule 6: Dosing (High)
  if (aiAvailable) {
    const hasDosing = aiResult.detectedContent?.complianceElements?.some((el: string) =>
      el.toLowerCase().includes('dose') || el.toLowerCase().includes('dosage') || 
      el.toLowerCase().includes('mg') || el.toLowerCase().includes('ml')
    )
    
    issues.push({
      rule: 'Dosing',
      severity: 'warning',
      field: 'dosing',
      section: 'FDA Compliance',
      channel: 'Social',
      status: hasDosing ? 'pass' : 'warning',
      message: hasDosing 
        ? 'Dosing information included'
        : 'Consider adding dosing information if making efficacy claims',
      detectedValue: hasDosing ? 'Present' : 'Not detected',
      expectedValue: 'Dosing info if making efficacy claims',
      source: 'AI Analysis',
      reasoning: 'AI analysis checked for dosage/dosing information'
    })
  } else {
    issues.push({
      rule: 'Dosing',
      severity: 'warning',
      field: 'dosing',
      section: 'FDA Compliance',
      channel: 'Social',
      status: 'warning',
      message: '⚠️ Manual review required - AI analysis not configured',
      detectedValue: 'AI not available',
      expectedValue: 'Configure OPENAI_API_KEY or GEMINI_API_KEY',
      reasoning: 'Requires AI analysis to verify dosing info',
      reasoning: 'This rule needs AI to detect dosing information',
      source: 'Built-in'
    })
  }

  // Rule 7: References (High)
  if (aiAvailable) {
    const hasReferences = aiResult.detectedContent?.complianceElements?.some((el: string) =>
      el.toLowerCase().includes('reference') || el.toLowerCase().includes('study') ||
      el.toLowerCase().match(/\d{4}/) // Year pattern for citations
    )
    
    issues.push({
      rule: 'References',
      severity: 'warning',
      field: 'references',
      section: 'FDA Compliance',
      channel: 'Social',
      status: hasReferences ? 'pass' : 'fail',
      message: hasReferences 
        ? 'References detected for claims'
        : 'Claims require supporting references - please add',
      detectedValue: hasReferences ? 'References present' : 'No references found',
      expectedValue: 'References for all claims',
      source: 'AI Analysis',
      reasoning: 'AI analysis checked for reference citations or study mentions'
    })
  } else {
    issues.push({
      rule: 'References',
      severity: 'warning',
      field: 'references',
      section: 'FDA Compliance',
      channel: 'Social',
      status: 'warning',
      message: '⚠️ Manual review required - AI analysis not configured',
      detectedValue: 'AI not available',
      expectedValue: 'Configure OPENAI_API_KEY or GEMINI_API_KEY',
      reasoning: 'Requires AI analysis to verify references',
      reasoning: 'This rule needs AI to detect reference citations',
      source: 'Built-in'
    })
  }

  // Rule 8: Copyright (High)
  const hasCopyrightInName = asset.name?.match(/©|copyright|\(c\)|®|™/i)
  if (aiAvailable) {
    const hasCopyright = hasCopyrightInName || aiResult.detectedContent?.complianceElements?.some((el: string) =>
      el.toLowerCase().includes('copyright') || el.toLowerCase().includes('©')
    )
    
    issues.push({
      rule: 'Copyright',
      severity: 'warning',
      field: 'copyright',
      section: 'Legal Requirements',
      channel: 'Social',
      status: hasCopyright ? 'pass' : 'warning',
      message: hasCopyright 
        ? 'Copyright notice detected'
        : 'Verify copyright notice is present',
      detectedValue: hasCopyright ? 'Present' : 'Not detected',
      expectedValue: '© Year Company Name',
      source: aiAvailable ? 'AI Analysis' : 'Built-in',
      reasoning: 'Checked for copyright symbols and notices'
    })
  } else {
    issues.push({
      rule: 'Copyright',
      severity: 'warning',
      field: 'copyright',
      section: 'Legal Requirements',
      channel: 'Social',
      status: 'warning',
      message: '⚠️ Manual review required - AI analysis not configured',
      detectedValue: 'AI not available',
      expectedValue: 'Configure OPENAI_API_KEY or GEMINI_API_KEY',
      reasoning: 'Requires AI analysis to verify copyright',
      reasoning: 'This rule needs AI to detect copyright notices',
      source: 'Built-in'
    })
  }

  // Rule 9: Font Size (Medium)
  if (aiAvailable && aiResult.detectedContent?.textElements) {
    const hasSmallText = aiResult.qualityAssessment?.potentialIssues?.some((issue: string) =>
      issue.toLowerCase().includes('font') || issue.toLowerCase().includes('text size') ||
      issue.toLowerCase().includes('small') || issue.toLowerCase().includes('legib')
    )
    
    issues.push({
      rule: 'Font Size',
      severity: 'minor',
      field: 'fontSize',
      section: 'Accessibility',
      channel: 'Social',
      status: hasSmallText ? 'warning' : 'pass',
      message: hasSmallText 
        ? 'Some text may be too small - verify legibility'
        : 'Text appears legible',
      detectedValue: hasSmallText ? 'Small text detected' : 'Text size acceptable',
      expectedValue: 'Minimum 6pt font for ISI',
      source: 'AI Analysis',
      reasoning: 'AI analysis checked for legibility issues'
    })
  } else {
    issues.push({
      rule: 'Font Size',
      severity: 'minor',
      field: 'fontSize',
      section: 'Accessibility',
      channel: 'Social',
      status: 'warning',
      message: '⚠️ Manual review required - AI analysis not configured',
      detectedValue: 'AI not available',
      expectedValue: 'Configure OPENAI_API_KEY or GEMINI_API_KEY',
      reasoning: 'Requires AI analysis to verify font sizes',
      reasoning: 'This rule needs AI to analyze text legibility',
      source: 'Built-in'
    })
  }

  // ==========================================
  // PROCESS GEMINI VIDEO ANALYSIS RESULTS
  // ==========================================
  if (asset.type === 'video' && aiResult.complianceIssues && Array.isArray(aiResult.complianceIssues)) {
    console.log('[Compliance] Processing Gemini video compliance issues:', aiResult.complianceIssues.length)
    
    aiResult.complianceIssues.forEach((geminiIssue: any) => {
      // Map Gemini issue to our format
      issues.push({
        rule: geminiIssue.rule || 'Video Compliance Issue',
        severity: geminiIssue.severity || 'warning',
        field: geminiIssue.field || 'video',
        section: geminiIssue.section || 'Video Analysis',
        channel: geminiIssue.channel || 'OLV/CTV',
        status: geminiIssue.status || 'warning',
        message: geminiIssue.finding || geminiIssue.message || 'Issue detected in video',
        detectedValue: geminiIssue.detectedValue,
        expectedValue: geminiIssue.expectedValue,
        source: 'AI Analysis',
        reasoning: geminiIssue.reasoning || 'Detected via Gemini video analysis',
        // Timestamp fields
        timestamp: geminiIssue.timestamp,
        timestampSeconds: geminiIssue.timestampSeconds,
        endTimestamp: geminiIssue.endTimestamp,
        endTimestampSeconds: geminiIssue.endTimestampSeconds,
        duration: geminiIssue.duration,
        videoFrame: geminiIssue.videoFrame,
        audioContent: geminiIssue.audioContent
      })
    })
  }

  // ==========================================
  // CUSTOM BRD RULES (User-Defined)
  // ==========================================
  console.log('[Compliance] Checking', customBRDRules.length, 'custom BRD rules')

  customBRDRules.forEach((brdRule) => {
    const ruleText = brdRule.rule.toLowerCase()
    const ruleField = brdRule.field || 'general'
    const ruleSection = brdRule.section || 'Custom Compliance'

    let ruleStatus: 'pass' | 'fail' | 'warning' | 'not_checked' = 'warning'
    let ruleMessage = '⚠️ Manual review required'
    let reasoning = 'Automated checking not available for this rule type'
    let detectedValue: string | undefined = 'Requires verification'
    let expectedValue: string | undefined = 'Compliant'

    // Check if AI has data for this rule
    if (aiAvailable && aiResult.detectedContent) {
      const hasComplianceElements = aiResult.detectedContent.complianceElements?.length > 0
      
      // Try to intelligently match AI results to custom rule
      if (hasComplianceElements) {
        const relevantElement = aiResult.detectedContent.complianceElements.find((el: string) =>
          el.toLowerCase().includes(ruleText) || ruleText.includes(el.toLowerCase().split(' ')[0])
        )
        
        if (relevantElement) {
          ruleStatus = 'pass'
          ruleMessage = `Compliance element detected: ${relevantElement}`
          detectedValue = relevantElement
          expectedValue = brdRule.rule
          reasoning = 'AI analysis found relevant compliance information for this custom rule'
        } else {
          ruleStatus = 'warning'
          ruleMessage = 'Compliance element not clearly detected - requires manual review'
          detectedValue = 'Not detected'
          expectedValue = brdRule.rule
          reasoning = 'AI did not find specific match for this custom rule'
        }
      } else {
        ruleStatus = 'warning'
        ruleMessage = 'Requires manual review - AI analysis incomplete for this rule'
        reasoning = 'AI analysis available but did not return specific compliance elements'
      }
    } else {
      ruleStatus = 'warning'
      ruleMessage = '⚠️ Manual review required - AI analysis not configured'
      detectedValue = 'AI not available'
      expectedValue = 'Configure OPENAI_API_KEY or GEMINI_API_KEY'
      reasoning = 'This custom rule needs AI analysis to verify compliance'
    }

    issues.push({
      rule: brdRule.rule,
      severity: brdRule.severity,
      field: ruleField,
      section: ruleSection,
      channel: 'All',
      status: ruleStatus,
      message: ruleMessage,
      detectedValue,
      expectedValue,
      reasoning,
      source: 'Custom BRD'
    })
  })

  // ==========================================
  // FINALIZE RESULTS
  // ==========================================

  // Add unique IDs to all issues
  const issuesWithIds = issues.map((issue, idx) => ({
    ...issue,
    id: idx + 1
  }))

  console.log('[Compliance] Completed checking. Total issues:', issuesWithIds.length)

  // Calculate scores
  const passedCount = issuesWithIds.filter(i => i.status === 'pass').length
  const totalChecks = issuesWithIds.length
  const failedCount = issuesWithIds.filter(i => i.status === 'fail').length
  const warningCount = issuesWithIds.filter(i => i.status === 'warning').length
  
  // Only count passed items for score calculation
  const complianceScore = totalChecks > 0 ? Math.round((passedCount / totalChecks) * 100) : 0

  // Generate recommendations
  const recommendations: string[] = []
  
  if (!aiAvailable) {
    recommendations.push(`🤖 Configure AI (OPENAI_API_KEY or GEMINI_API_KEY) for automated compliance analysis`)
  }
  
  if (failedCount > 0) {
    recommendations.push(`⚠️ Address ${failedCount} failed compliance check(s) before trafficking`)
  }
  
  if (warningCount > 0 && aiAvailable) {
    recommendations.push(`📋 Review ${warningCount} warning item(s) for optimal compliance`)
  } else if (warningCount > 0 && !aiAvailable) {
    recommendations.push(`📋 ${warningCount} item(s) require manual review until AI is configured`)
  }
  
  if (failedCount === 0 && warningCount === 0) {
    recommendations.push('✅ Asset passed all compliance checks - ready for trafficking')
  }
  
  if (customBRDRules.length > 0) {
    recommendations.push(`📚 Checked against ${customBRDRules.length} custom BRD rule(s)`)
  }

  return {
    campaignName: aiResult.campaignSuggestions?.campaignName || aiResult.detectedContent?.brandName || 'AI Generated Campaign',
    fdaFilingNumber: aiResult.detectedContent?.complianceElements?.find((el: string) => el.includes('PP-')) || 'PP-XXX-USA-0000',
    customerType: aiResult.detectedContent?.targetAudience || 'HCP',
    complianceScore,
    issues: issuesWithIds,
    recommendations,
    summary: {
      totalChecks,
      passed: passedCount,
      failed: failedCount,
      warnings: warningCount
    }
  }
}
